import { useEffect, useState } from 'react';

export interface PublicConfig {
  billingEnabled: boolean;
  /** True once Stripe is connected; until then paid plans are requested, not bought. */
  stripeReady: boolean;
  sitesUrlMode: 'subdomain' | 'path';
}

// Until the server answers, assume the cautious defaults so prices never flash up before we know they exist.
const DEFAULTS: PublicConfig = { billingEnabled: false, stripeReady: false, sitesUrlMode: 'subdomain' };

let cached: PublicConfig | null = null;
let pending: Promise<PublicConfig> | null = null;

export function loadConfig(): Promise<PublicConfig> {
  if (cached) return Promise.resolve(cached);
  if (!pending) {
    pending = fetch('/api/v1/config', { credentials: 'same-origin' })
      .then((res) => (res.ok ? (res.json() as Promise<PublicConfig>) : DEFAULTS))
      .then((value) => {
        cached = { ...DEFAULTS, ...value };
        return cached;
      })
      .catch(() => DEFAULTS);
  }
  return pending;
}

export function useConfig(): PublicConfig {
  const [config, setConfig] = useState<PublicConfig>(cached ?? DEFAULTS);
  useEffect(() => {
    let active = true;
    void loadConfig().then((value) => {
      if (active) setConfig(value);
    });
    return () => {
      active = false;
    };
  }, []);
  return config;
}
